<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <!-- Invalid / expired token -->
      <b-card v-if="tokenError" header="Recovery Link Invalid" header-bg-variant="danger" header-text-variant="white" bg-variant="light">
        <p class="mb-2">{{ tokenError }}</p>
        <p class="mb-3 text-muted" style="font-size:0.9rem;">
          Please return to the Account Recovery page and submit your details again to receive a new link.
        </p>
        <div class="text-right">
          <b-button size="sm" variant="secondary" :to="{ name: 'account-recovery' }">Account Recovery</b-button>
        </div>
      </b-card>

      <!-- Loading -->
      <b-card v-else-if="verifying" header="Verifying…" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <div class="text-center py-3"><b-spinner /></div>
      </b-card>

      <!-- Success state (shown when dup-email warning needs acknowledgement) -->
      <b-card v-else-if="recovered" header="Account Recovered" header-bg-variant="success" header-text-variant="white" bg-variant="light">
        <b-alert show variant="success" class="mb-3">
          <b-icon icon="check-circle-fill" class="mr-2" />
          Your account has been successfully recovered. You may now log in with your new password.
        </b-alert>
        <b-alert v-if="dupEmailWarning" show variant="warning" class="mb-3">
          <b-icon icon="exclamation-triangle-fill" class="mr-2" />
          The email address provided is being used by another account. Please update your contact information after you log in.
          <strong>IMPORTANT:</strong> If you do not update the email address, please make sure you have access to this email.
          You will need access to the email to reset your password or request your username.
        </b-alert>
        <div class="text-right">
          <b-button size="sm" variant="primary" :to="{ name: 'login' }">Continue to Login</b-button>
        </div>
      </b-card>

      <!-- Recovery form -->
      <b-card v-else header="Recover Your Account" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <b-alert show variant="success" class="mb-3">
          <b-icon icon="check-circle-fill" class="mr-2" />
          Identity verified for
          <strong>{{ email }}</strong>
          .
          <template v-if="ubcid">
            <br />
            <small>
              UBC ID:
              <strong>{{ ubcid }}</strong>
            </small>
          </template>
        </b-alert>

        <b-form @submit.prevent="onSubmit">
          <!-- Optional: new username -->
          <b-form-group label="Username:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="newUsername" :state="validated('newUsername')" placeholder="Username" trim />
            <b-form-invalid-feedback>Username must be at least 6 characters, contain at least one letter, and use only alphanumeric characters.</b-form-invalid-feedback>
            <b-form-text>Leave unchanged to keep your existing username.</b-form-text>
          </b-form-group>

          <!-- Optional: new email -->
          <b-form-group label="Email:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="newEmail" type="email" :state="validated('newEmail')" placeholder="Email Address" trim />
            <b-form-invalid-feedback>Please enter a valid email address.</b-form-invalid-feedback>
            <b-form-text>
              If you change this email address, please be sure you have access to this email because you will be asked to verify when you log in.
            </b-form-text>
          </b-form-group>

          <hr class="my-3" />

          <password-and-confirm-fields
            :password.sync="password"
            :confirm.sync="passwordConfirm"
            :password-state="validated('password')"
            :confirm-state="validated('passwordConfirm')"
            :password-feedback="passwordFeedback || ''"
            password-label="New Password:"
            password-placeholder="New Password"
          />

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" :to="{ name: 'login' }">Cancel</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading || submitDisabled">
              <b-spinner v-if="loading" small class="mr-1" />
              Recover My Account
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import { emailValidator } from '@/util/validator';
import passwordMixin from '@/mixins/password_mixin';
import PasswordAndConfirmFields from '@/components/PasswordAndConfirmFields';
import { renderErrorBody } from '@/util/error';

@Component({
  mixins: [passwordMixin],
  components: { PasswordAndConfirmFields },
  metaInfo: {
    title: 'Member Portal - Complete Recovery'
  }
})
export default class CompleteRecovery extends Vue {
  verifying = true;
  tokenError = null;
  loading = false;
  recovered = false;
  dupEmailWarning = false;
  token = '';
  email = '';
  ubcid = '';
  newUsername = '';
  newEmail = '';
  password = '';
  passwordConfirm = '';

  get memberPortalUrl() {
    return this.$store.getters.meta?.memberPortalUrl || 'https://member-portal.carpenters.org';
  }

  get submitDisabled() {
    const emailOk = !(this.newEmail || '').trim() || this.validated('newEmail') === true;
    const usernameOk = this.validated('newUsername') !== false;
    return !this.validated('password') || !this.validated('passwordConfirm') || !emailOk || !usernameOk;
  }

  validated(field) {
    switch (field) {
      case 'password':
        return !this.passwordValue.length ? undefined : !this.passwordFeedback;
      case 'passwordConfirm':
        return !this.passwordValue ? undefined : (this.passwordConfirm || '').trim().length > 0 && this.password === this.passwordConfirm;
      case 'newUsername': {
        const v = (this.newUsername || '').trim();
        if (v.length === 0) return undefined;
        return v.length >= 6 && /[a-zA-Z]/.test(v) && /^[a-zA-Z0-9]+$/.test(v);
      }
      case 'newEmail': {
        const v = (this.newEmail || '').trim();
        return v.length === 0 ? undefined : emailValidator(v);
      }
      default:
        return true;
    }
  }

  async mounted() {
    try {
      this.token = this.$route.query.token || '';
      if (!this.token) {
        throw new Error('No recovery token was found in the link. Please check your email and try again.');
      }
      const { email, ubcid, username } = await memberDao.verifyRecoveryToken(this.token);
      this.email = email || '';
      this.ubcid = ubcid || '';
      this.newUsername = username || '';
      this.newEmail = email || '';
    } catch (error) {
      this.tokenError = (error && error.message) || 'This account recovery link is invalid or has expired.';
    } finally {
      this.verifying = false;
    }
  }

  async onSubmit() {
    if (this.submitDisabled) return;
    try {
      this.loading = true;
      const payload = { token: this.token, password: this.password };
      if ((this.newUsername || '').trim()) payload.new_username = this.newUsername.trim();
      if ((this.newEmail || '').trim()) payload.new_email = this.newEmail.trim();
      const result = await memberDao.completeAccountRecovery(payload);
      if (result && result.dup_email_warning) {
        this.dupEmailWarning = true;
        this.recovered = true;
      } else {
        window.location.href = `${this.memberPortalUrl}/?account_recovered=1`;
      }
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Account Recovery Failed',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style scoped></style>
